import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useEffect } from 'react';

// Layout
import AdminLayout from '@/components/layout/AdminLayout';

// Pages
import Login from '@/pages/Login';
import MobileCallback from '@/pages/MobileCallback';
import SetPassword from '@/pages/SetPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Users from '@/pages/Users';
import UserDetailPage from '@/pages/UserDetailPage';
import Businesses from '@/pages/Businesses';
import BusinessDetailPage from '@/pages/BusinessDetailPage';
import Moderation from '@/pages/Moderation';
import Analytics from '@/pages/Analytics';
import GoogleAnalytics from '@/pages/GoogleAnalytics';
import SEOIndexing from '@/pages/SEOIndexing';
import Payments from '@/pages/Payments';
import AccessCodes from '@/pages/AccessCodes';
import PromoCodes from '@/pages/PromoCodes';
import PresetProfiles from '@/pages/PresetProfiles';
import BusinessControls from '@/pages/BusinessControls';
import BlogPosts from '@/pages/BlogPosts';
import BlogPostDetail from '@/pages/BlogPostDetail';
import BlogPostPreview from '@/pages/BlogPostPreview';
import BlogGenerator from '@/pages/BlogGenerator';
import DuplicateManager from '@/pages/DuplicateManager';
import BlogAuthors from '@/pages/BlogAuthors';
import BlogAuthorDetail from '@/pages/BlogAuthorDetail';
import EmailTemplates from '@/pages/EmailTemplates';
import EmailTemplateDetail from '@/pages/EmailTemplateDetail';
import Waitlist from '@/pages/Waitlist';
import AuditLogs from '@/pages/AuditLogs';
import UserActivityLog from '@/pages/UserActivityLog';
import LeadBilling from '@/pages/LeadBilling';
import TestNotificationPage from '@/pages/TestNotificationPage';
import FeedbackManagement from '@/pages/FeedbackManagement';
import JourneyVideos from '@/pages/JourneyVideos';
import ALaCarteOfferings from '@/pages/ALaCarteOfferings';
import Operations from '@/pages/Operations';
import AffiliateManagement from '@/pages/AffiliateManagement';
import AffiliatePartnerDetail from '@/pages/AffiliatePartnerDetail';
import AffiliatePortalDashboard from '@/pages/AffiliatePortalDashboard';
import AffiliatePortalReferrals from '@/pages/AffiliatePortalReferrals';
import AffiliatePortalCommissions from '@/pages/AffiliatePortalCommissions';
import AffiliatePortalPayouts from '@/pages/AffiliatePortalPayouts';
import AffiliatePortalProfile from '@/pages/AffiliatePortalProfile';
import HowToVideos from '@/pages/HowToVideos';
import Settings from '@/pages/Settings';
import NotFound from '@/pages/NotFound';

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

const MAX_INIT_TIMEOUT_MS = 30000; // 30 seconds max initialization time

export default function App() {
  const { initialize } = useAuthStore();

  useEffect(() => {
    initialize();

    // Failsafe: if still loading after 30 seconds, force stop loading
    const failsafeTimeout = setTimeout(() => {
      const state = useAuthStore.getState();
      if (state.isLoading) {
        console.error('Initialization timeout reached - forcing loading to false');
        useAuthStore.setState({
          isLoading: false,
          error: 'Initialization timed out. Please refresh the page.',
        });
      }
    }, MAX_INIT_TIMEOUT_MS);

    return () => clearTimeout(failsafeTimeout);
  }, [initialize]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/mobile-callback" element={<MobileCallback />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="users/:userId" element={<UserDetailPage />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="businesses/:businessId" element={<BusinessDetailPage />} />
        <Route path="preset-profiles" element={<PresetProfiles />} />
        <Route path="business-controls" element={<BusinessControls />} />
        <Route path="blog" element={<BlogPosts />} />
        <Route path="blog/generate" element={<BlogGenerator />} />
        <Route path="blog/duplicates" element={<DuplicateManager />} />
        <Route path="blog/preview/:postId" element={<BlogPostPreview />} />
        <Route path="blog/:postId" element={<BlogPostDetail />} />
        <Route path="blog-authors" element={<BlogAuthors />} />
        <Route path="blog-authors/:authorId" element={<BlogAuthorDetail />} />
        <Route path="email-templates" element={<EmailTemplates />} />
        <Route path="email-templates/:templateId" element={<EmailTemplateDetail />} />
        <Route path="moderation" element={<Moderation />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="google-analytics" element={<GoogleAnalytics />} />
        <Route path="seo-indexing" element={<SEOIndexing />} />
        <Route path="payments" element={<Payments />} />
        <Route path="lead-billing" element={<LeadBilling />} />
        <Route path="access-codes" element={<AccessCodes />} />
        <Route path="promo-codes" element={<PromoCodes />} />
        <Route path="waitlist" element={<Waitlist />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="user-activity" element={<UserActivityLog />} />
        <Route path="test-notification" element={<TestNotificationPage />} />
        <Route path="feedback" element={<FeedbackManagement />} />
        <Route path="operations" element={<Operations />} />
        <Route path="journey-videos" element={<JourneyVideos />} />
        <Route path="how-to-videos" element={<HowToVideos />} />
        <Route path="a-la-carte" element={<ALaCarteOfferings />} />
        <Route path="affiliates" element={<AffiliateManagement />} />
        <Route path="affiliates/:partnerId" element={<AffiliatePartnerDetail />} />
        <Route path="portal" element={<AffiliatePortalDashboard />} />
        <Route path="portal/referrals" element={<AffiliatePortalReferrals />} />
        <Route path="portal/commissions" element={<AffiliatePortalCommissions />} />
        <Route path="portal/payouts" element={<AffiliatePortalPayouts />} />
        <Route path="portal/profile" element={<AffiliatePortalProfile />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
